/**
 * GA4MeasurementProtocolEditor - Configuration editor for GA4 Measurement Protocol (Server-Side)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  Link,
  Alert,
  FormControlLabel,
  FormHelperText,
  Switch,
} from '@mui/material';
import type { GA4MeasurementProtocolConfig, PlatformConfig } from '../../types/analytics.types';

interface GA4MeasurementProtocolEditorProps {
  config: PlatformConfig;
  onChange: (config: PlatformConfig, name?: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  showSaveButton?: boolean;
  onSave?: (config: PlatformConfig, name?: string) => void;
  name?: string;
}

export const GA4MeasurementProtocolEditor: React.FC<GA4MeasurementProtocolEditorProps> = ({
  config,
  onChange,
  disabled = false,
  readOnly = false,
  showSaveButton = false,
  onSave,
  name,
}) => {
  const ga4Config = config as GA4MeasurementProtocolConfig;
  const [localConfig, setLocalConfig] = useState<GA4MeasurementProtocolConfig>(ga4Config);
  const [localName, setLocalName] = useState(name || '');

  useEffect(() => {
    setLocalConfig(ga4Config);
    setLocalName(name || '');
  }, [ga4Config, name]);

  const handleChange = useCallback(
    (field: keyof GA4MeasurementProtocolConfig, value: unknown) => {
      const newConfig = { ...localConfig, [field]: value };
      setLocalConfig(newConfig);
      if (!showSaveButton) {
        onChange(newConfig, localName);
      }
    },
    [localConfig, localName, onChange, showSaveButton]
  );

  const handleSave = useCallback(() => {
    if (onSave) {
      onSave(localConfig, localName);
    }
  }, [onSave, localConfig, localName]);

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        GA4 Measurement Protocol sends purchase events server-side for accurate revenue tracking.
        This supplements the client-side GA4 tracking.
      </Alert>

      <TextField
        label="Measurement ID"
        value={localConfig.measurementId || ''}
        onChange={(e) => handleChange('measurementId', e.target.value)}
        disabled={disabled || readOnly}
        fullWidth
        required
        sx={{ mb: 2 }}
        placeholder="G-XXXXXXXXXX"
        helperText={
          <>
            Same Measurement ID as your client-side GA4. Find it in{' '}
            <Link
              href="https://analytics.google.com/"
              target="_blank"
              rel="noopener"
            >
              GA4 Admin → Data Streams → Web
            </Link>
          </>
        }
      />

      <TextField
        label="API Secret"
        value={localConfig.apiSecret || ''}
        onChange={(e) => handleChange('apiSecret', e.target.value)}
        disabled={disabled || readOnly}
        fullWidth
        required
        type="password"
        sx={{ mb: 2 }}
        placeholder="xxxxxxxxxxxxxxxx"
        helperText={
          <>
            Create in{' '}
            <Link
              href="https://analytics.google.com/"
              target="_blank"
              rel="noopener"
            >
              GA4 Admin → Data Streams → Measurement Protocol API secrets
            </Link>
          </>
        }
      />

      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={localConfig.renewalsAsPurchase ?? true}
              onChange={(e) => handleChange('renewalsAsPurchase', e.target.checked)}
              disabled={disabled || readOnly}
            />
          }
          label="Send subscription renewals as purchase"
        />
        <FormHelperText sx={{ ml: 0 }}>
          ON: renewals count as the standard &quot;purchase&quot; event. OFF: renewals arrive as
          custom &quot;recurring_payment&quot; event (excluded from purchase revenue reports).
        </FormHelperText>
      </Box>

      {showSaveButton && (
        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={disabled || !localConfig.measurementId || !localConfig.apiSecret}
          >
            Save
          </Button>
        </Box>
      )}
    </Box>
  );
};
